"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function ReloaderPage({ onComplete }) {
  const [step, setStep] = useState("logo"); // 'logo' -> 'text' -> 'exit'

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      if (onComplete) onComplete();
      return;
    }

    // Step 1: Logo rises up (0ms -> 1000ms)
    // Step 2: Big writing bursts/expands out from the logo (1000ms -> 2500ms)
    const textTimer = setTimeout(() => {
      setStep("text");
    }, 1100);

    // Step 3: Elegant curtain exit (2700ms -> 3400ms)
    const exitTimer = setTimeout(() => {
      setStep("exit");
    }, 2800);

    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 3600);

    return () => {
      clearTimeout(textTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  return (
    <AnimatePresence>
      {step !== "exit" && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            y: -30,
            transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] },
          }}
          className="fixed inset-0 z-[99999] pointer-events-none flex items-center justify-center overflow-hidden"
          style={{ backgroundColor: "#f5f7fa" }}
        >
          {/* DOT GRID — engineering blueprint feel */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle, rgba(0,102,153,0.18) 1px, transparent 1px)`,
              backgroundSize: "28px 28px",
            }}
          />

          {/* SECONDARY LARGE DOT GRID — layered for depth */}
          <div
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              backgroundImage: `radial-gradient(circle, rgba(0,102,153,0.08) 1.5px, transparent 1.5px)`,
              backgroundSize: "84px 84px",
              backgroundPosition: "14px 14px",
            }}
          />

          {/* BLUE RADIAL EDGE VIGNETTE — gives depth, not flat */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at center, transparent 40%, rgba(0,102,153,0.07) 100%)`,
            }}
          />

          {/* SOFT CENTER GLOW */}
          <div className="absolute w-[600px] h-[600px] rounded-full bg-[#006699]/[0.05] blur-3xl pointer-events-none" />

          {/* GIANT EDITORIAL BACKGROUND WATERMARK (expands subtly) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{
              opacity: step === "text" ? 0.08 : 0,
              scale: step === "text" ? 1.05 : 0.85,
            }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 flex items-center justify-center select-none pointer-events-none overflow-hidden"
          >
            <span className="text-[18vw] font-black tracking-tighter text-[#006699] uppercase whitespace-nowrap leading-none">
              IEEE ISIMS
            </span>
          </motion.div>

          {/* MAIN STAGE */}
          <div className="relative z-10 flex flex-col items-center justify-center w-full px-6 text-center">
            {/* 1. THE LOGO: Rises up slowly and smoothly */}
            <motion.div
              initial={{ y: 90, opacity: 0, scale: 0.8 }}
              animate={{
                y: step === "text" ? -20 : 0,
                opacity: 1,
                scale: step === "text" ? 0.9 : 1,
              }}
              transition={{
                duration: 1.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="relative mb-4 flex items-center justify-center"
            >
              <img
                src="/logosb.png"
                alt="IEEE ISIMS SB Logo"
                className="w-28 sm:w-36 md:w-44 h-auto object-contain"
                style={{
                  filter:
                    "brightness(0) saturate(100%) invert(28%) sepia(29%) saturate(1337%) hue-rotate(158deg) drop-shadow(0 10px 25px rgba(0,102,153,0.35))",
                }}
              />
            </motion.div>

            {/* 2. BIG WRITING: Emerges out from the logo across the screen */}
            {step === "text" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.6, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{
                  duration: 0.95,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="flex flex-col items-center justify-center max-w-full"
              >
                {/* Big typography across the screen */}
                <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-black text-[#002233] tracking-tighter leading-none uppercase">
                  IEEE <span className="text-[#006699]">ISIMS</span> SB
                </h1>

                {/* Accent line expanding outwards from center */}
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "140px" }}
                  transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
                  className="h-[3px] bg-gradient-to-r from-transparent via-[#006699] to-transparent my-4 sm:my-5"
                />

               
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}